//Layout
import Header from 'layout/header/Header';
import Capsule from 'layout/Capsule';

//Components
import OffcanvasEditUser from './components/OffcanvasEditUser';
import OffCanvasAddUser from './components/OffCanvasAddUser';
import FilterInput from 'components/FilterInput';
import { IoAdd, IoRepeat } from 'react-icons/io5';
import PlaceHolderItemList from 'components/loader/PlaceHolderItemList';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

//Assets
import defaultAvatar from 'assets/img/avatar_default.jpg';

//Logic
import useUsers from './useUsers';
import { useFilterInput } from 'hooks/useFilterInput';
import { mapRoles } from 'utils/helpers';
import { useLocation, useNavigate } from 'react-router';
import { useMemo, useState } from 'react';
import moment from 'moment';

function UserItem({ user, dispatchEditUser, isSkeleton }) {
    const registrationDate = user?.created_at ? moment(user.created_at).format('DD/MM/YY') : '-';
    const roleLabel = !!user?.roles?.length ? mapRoles(user.roles).join(', ') : 'Non attivo';
    const roleColorClass = !user?.roles?.length || user.roles.includes('agent') ? 'gray' : `${user.is_system ? 'danger' : 'warning'}`;

    return (
        <li onClick={() => !isSkeleton && dispatchEditUser(user.id)}>
            <div className="item">
                <div className="avatar me-2 position-relative">
                    <img src={user?.avatar_url || defaultAvatar} alt="avatar" className="imaged rounded w36" />
                    {!isSkeleton && user?.has_organization === false && (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`user-no-org-${user?.id || 'no-org'}`}>Nessuna attività</Tooltip>}
                        >
                            <span
                                className="badge badge-warning badge-empty position-absolute"
                                style={{
                                    top: '0px',
                                    right: '0px',
                                    border: '2px solid #fff',
                                    boxSizing: 'content-box'
                                }}
                            />
                        </OverlayTrigger>
                    )}
                </div>
                {isSkeleton ? <PlaceHolderItemList hideSecondLine /> : <>
                    <div className="in">
                        <div>
                            <span>{user.display_name}</span>
                            <div className="d-flex align-items-center flex-wrap gap-1">
                                <span className={`text-12 text-${roleColorClass}`}>
                                    {roleLabel}
                                </span>
                                <span className="text-muted text-12">•</span>
                                <span className="text-muted" style={{ fontSize: '10px' }}>
                                    Registrato il {registrationDate}
                                </span>
                            </div>
                        </div>
                    </div>
                </>}
            </div>
        </li>
    );
}

export default function Users() {

    const location = useLocation();
    const navigate = useNavigate();

    const {
        users,
        editUser, dispatchEditUser,
        showOffCanvasAddUser, dispatchAddUser,
        onAddUser,
        onUpdateUser
    } = useUsers();

    const [
        filterInputValue,
        handleFilterInputChange,
        filterSearchArray
    ] = useFilterInput(user => {
        return `${user.display_name}${user.uid}`;
    });

    const [sortByCreatedAt, setSortByCreatedAt] = useState(true);
    const [organizationFilter, setOrganizationFilter] = useState('all');
    const totalUsers = users ? users.length : 0;
    const usersCountByOrganization = useMemo(() => {
        if (!users) {
            return { all: 0, withOrganization: 0, withoutOrganization: 0 };
        }
        const withOrganization = users.filter(u => u?.has_organization === true).length;
        const withoutOrganization = users.filter(u => u?.has_organization === false).length;
        return {
            all: users.length,
            withOrganization,
            withoutOrganization
        };
    }, [users]);

    const filteredAndSortedUsers = useMemo(() => {
        if (!users) return [];
        return [...users]
            .filter(user => {
                if (organizationFilter === 'with') return user?.has_organization === true;
                if (organizationFilter === 'without') return user?.has_organization === false;
                return true;
            })
            .filter(filterSearchArray)
            .sort((a, b) => {
                if (sortByCreatedAt) {
                    return new Date(b.created_at) - new Date(a.created_at);
                }
                return (a.display_name || '').localeCompare((b.display_name || ''), 'it', { sensitivity: 'base' });
            });
    }, [users, filterSearchArray, sortByCreatedAt, organizationFilter]);

    return (
        <>
            <Header
                show_agency={false}
                pageTitle={totalUsers > 0 ? `Lista utenti (${totalUsers})` : 'Lista utenti'}
                handleBack={location?.state?.from ? () => navigate( location?.state?.from) : null}
            />
            <Capsule>
                <FilterInput 
                    placeHolder="Ricerca per nome o cognome"
                    onChange={handleFilterInputChange}
                    value={filterInputValue}
                />
                <div className="section mb-2">
                    <Button
                        size="sm"
                        variant="github"
                        className='btn-block rounded-2 text-uppercase'
                        onClick={() => setSortByCreatedAt(!sortByCreatedAt)}
                    >
                        <IoRepeat /> Ordina per {sortByCreatedAt ? 'Nome' : 'Data'}
                    </Button>
                </div>
                <div className="section mb-2">
                    <div className="btn-group w-100 users-filter-group" role="group" aria-label="Filtra utenti per attività">
                        <button
                            type="button"
                            className={`btn btn-sm ${organizationFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setOrganizationFilter('all')}
                        >
                            Tutti ({usersCountByOrganization.all})
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm ${organizationFilter === 'with' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setOrganizationFilter('with')}
                        >
                            Con attività ({usersCountByOrganization.withOrganization})
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm ${organizationFilter === 'without' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setOrganizationFilter('without')}
                        >
                            Senza attività ({usersCountByOrganization.withoutOrganization})
                        </button>
                    </div>
                </div>
                <div className="section">
                    <ul className="listview image-listview rounded">
                        {<li className='cursor-pointer' onClick={() => dispatchAddUser('OPEN')}>
                            <div className="item">
                                <div className="icon-box bg-success">
                                    <IoAdd />
                                </div>
                                <div className="in text-success">
                                    <div className="fw-500">Aggiungi utente</div>
                                </div>
                            </div>
                        </li>}
                        {!users && new Array(4).fill(null).map((_, index) => (
                            <UserItem
                                key={'SKELETON' + index}
                                isSkeleton
                            />
                        ))}
                        {users && filteredAndSortedUsers.map(user => <UserItem 
                            key={user.id}
                            user={user}
                            dispatchEditUser={dispatchEditUser}
                        />)}
                    </ul>
                </div>

            </Capsule>
            <OffcanvasEditUser
                editUser={editUser}
                handleClose={() => dispatchEditUser('CLOSE')}
                onUpdateUser={onUpdateUser}
            />
            <OffCanvasAddUser 
                show={showOffCanvasAddUser}
                handleClose={() => dispatchAddUser('CLOSE')}
                onAddUser={onAddUser}
            />
        </>

    );
}
